import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Clock, CalendarCheck, MousePointerClick, Gauge, CheckCircle2, Star,
  Wifi, Coffee, MapPin, Building2, ConciergeBell, ShieldCheck, ArrowRight, Sparkles,
} from "lucide-react";
import { api, formatBRL } from "../../lib/api";
import { hourBankName, canonicalCategory, packagePricing } from "../../lib/hourbank";

const STEPS = [
  { icon: MousePointerClick, title: "Escolha o plano", text: "Selecione o Banco de Horas ideal para sua rotina — de 4h a 96h." },
  { icon: CalendarCheck, title: "Reserve", text: "Agende o espaço online em segundos, sem burocracia." },
  { icon: Clock, title: "Utilize", text: "Suas horas são debitadas automaticamente. Sem PIX, sem espera." },
  { icon: Gauge, title: "Controle tudo online", text: "Acompanhe saldo, consumo e validade em tempo real no portal." },
];

const BENEFITS = [
  { icon: Building2, label: "Sem aluguel" },
  { icon: ShieldCheck, label: "Sem condomínio" },
  { icon: Wifi, label: "Internet inclusa" },
  { icon: ConciergeBell, label: "Recepção profissional" },
  { icon: Coffee, label: "Café à vontade" },
  { icon: MapPin, label: "Endereço comercial" },
  { icon: Star, label: "Salas premium" },
  { icon: CalendarCheck, label: "Reserva online" },
  { icon: Gauge, label: "Controle em tempo real" },
];

const FAQS = [
  { q: "Como funciona o Banco de Horas?", a: "Você compra um pacote de horas (4h a 96h) e usa quando quiser, dentro da validade. Ao reservar um espaço da categoria do seu banco, as horas são debitadas automaticamente — sem nova cobrança." },
  { q: "As horas têm validade?", a: "Sim. Cada Banco de Horas tem validade própria (exibida no plano). Você acompanha a data limite e o saldo em tempo real no Portal do Cliente." },
  { q: "E se as minhas horas acabarem?", a: "Você é avisado por WhatsApp e e-mail quando o saldo estiver baixo. Pode recomprar em poucos cliques ou reservar avulso via PIX normalmente." },
  { q: "Posso usar em qualquer espaço?", a: "O Banco de Horas vale para os espaços da categoria contratada (Coworking, Escritório Executivo ou Consultório). Salas de reunião podem ser reservadas avulsas." },
  { q: "Como pago?", a: "Pagamento via PIX com confirmação automática. Assim que o pagamento é aprovado, seu Banco de Horas é ativado na hora." },
];

export default function BancoDeHoras() {
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [spaces, setSpaces] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/public/plans").then(({ data }) => setPlans(data.filter(p => p.pricing_mode === "package" && (p.price_package || 0) > 0))),
      api.get("/public/spaces").then(({ data }) => setSpaces(data)),
    ]).catch(() => {});
  }, []);

  const grouped = useMemo(() => {
    const byCat = {};
    plans.forEach(p => {
      const c = canonicalCategory(p);
      (byCat[c] = byCat[c] || []).push(p);
    });
    Object.values(byCat).forEach(l => l.sort((a, b) => (a.package_hours || 999) - (b.package_hours || 999)));
    return byCat;
  }, [plans]);

  // Comparativo: avulso vs melhor Banco de Horas (Business) por categoria
  const comparison = useMemo(() => {
    return Object.entries(grouped).map(([cat, list]) => {
      const business = list.find(p => p.package_hours === 64) || list[list.length - 1];
      if (!business) return null;
      const pricing = packagePricing(business, spaces);
      if (!pricing || !pricing.rate || pricing.rate <= pricing.perHour) return null;
      return { cat, rate: pricing.rate, perHour: pricing.perHour, save: pricing.save };
    }).filter(Boolean);
  }, [grouped, spaces]);

  return (
    <div data-testid="banco-de-horas-page" className="pb-0">
      {/* ═══ HERO ═══ */}
      <section className="pt-28 pb-20 bg-[#0A1128] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #D4AF37 0, transparent 40%)" }} />
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center relative">
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-6 border border-[#D4AF37]/30 rounded-full px-4 py-1.5">
            <Sparkles size={12} /> Banco de Horas VM Corporate
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white font-light leading-tight">
            Seu escritório
            <span className="block italic text-[#D4AF37] mt-2">quando você precisa.</span>
          </h1>
          <p className="text-base sm:text-lg text-white/60 mt-6 max-w-xl mx-auto leading-relaxed">
            Pague apenas pelas horas que utilizar.<br />
            <strong className="text-white/80">Sem aluguel. Sem desperdício.</strong>
          </p>
          <a href="#planos" className="btn-gold inline-flex items-center gap-2 px-8 py-4 rounded-md text-sm uppercase tracking-wider font-bold mt-10" data-testid="hero-cta">
            Escolher meu Banco de Horas <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* ═══ COMO FUNCIONA ═══ */}
      <section className="py-20 bg-[#0D1530]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-3 text-center">Como funciona</div>
          <h2 className="font-display text-2xl sm:text-3xl text-white text-center mb-14">Simples do início ao fim</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="how-it-works">
            {STEPS.map((s, i) => (
              <div key={i} className="card-marketing p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center mx-auto mb-4">
                  <s.icon size={20} />
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-1">Passo {i + 1}</div>
                <div className="font-bold text-white mb-2">{s.title}</div>
                <p className="text-sm text-white/55 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ COMPARATIVO ═══ */}
      {comparison.length > 0 && (
        <section className="py-20 bg-[#0A1128]">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-3 text-center">Comparativo</div>
            <h2 className="font-display text-2xl sm:text-3xl text-white text-center mb-10">Reserva Avulsa × Banco de Horas</h2>
            <div className="overflow-x-auto rounded-lg border border-white/10" data-testid="comparison-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-white/5 text-left text-xs uppercase tracking-wider text-white/50">
                    <th className="px-5 py-4">Categoria</th>
                    <th className="px-5 py-4">Avulso (por hora)</th>
                    <th className="px-5 py-4 text-[#D4AF37]">Banco de Horas (por hora)</th>
                    <th className="px-5 py-4">Economia no pacote 64h</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((c) => (
                    <tr key={c.cat} className="border-t border-white/5 text-white/80">
                      <td className="px-5 py-4 font-semibold text-white">{c.cat}</td>
                      <td className="px-5 py-4"><s className="text-white/40">{formatBRL(c.rate)}</s></td>
                      <td className="px-5 py-4 text-[#D4AF37] font-bold">{formatBRL(c.perHour)}</td>
                      <td className="px-5 py-4 text-emerald-400 font-bold">Economize {formatBRL(c.save)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* ═══ BENEFÍCIOS ═══ */}
      <section className="py-20 bg-[#0D1530]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-3 text-center">Benefícios</div>
          <h2 className="font-display text-2xl sm:text-3xl text-white text-center mb-12">Tudo incluso. Zero custo fixo.</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="benefits-grid">
            {BENEFITS.map((b, i) => (
              <div key={i} className="flex items-center gap-3 card-marketing px-4 py-3.5">
                <b.icon size={17} className="text-[#D4AF37] shrink-0" />
                <span className="text-sm text-white/80">{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PLANOS ═══ */}
      <section id="planos" className="py-20 bg-[#0A1128]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-3 text-center">Planos</div>
          <h2 className="font-display text-2xl sm:text-3xl text-white text-center mb-14">Escolha seu Banco de Horas</h2>
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} className="mb-14">
              <h3 className="font-display text-xl text-white mb-6">{cat}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {list.map((p) => {
                  const pricing = packagePricing(p, spaces);
                  const featured = p.package_hours === 32;
                  return (
                    <div key={p.id} className={`card-marketing p-5 flex flex-col ${featured ? "border-[#D4AF37]/70 bg-[#D4AF37]/5" : ""}`} data-testid={`bh-plan-${p.id}`}>
                      {featured && (
                        <div className="inline-flex items-center gap-1 self-start mb-3 px-2.5 py-1 bg-[#D4AF37] text-[#0A1128] text-[9px] uppercase tracking-[0.15em] font-bold rounded-full">
                          <Star size={10} /> Mais vendido
                        </div>
                      )}
                      <div className="font-bold text-white text-sm leading-snug mb-3">{hourBankName(p)}</div>
                      <div className="font-display text-2xl text-[#D4AF37]">{formatBRL(pricing?.perHour ?? (p.price_package / p.package_hours))}<span className="text-sm text-white/50"> /hora</span></div>
                      <div className="text-[11px] text-white/50 mt-1">{p.package_hours} horas · Total {formatBRL(pricing?.total ?? p.price_package)}</div>
                      {pricing?.save > 0.5 && (
                        <div className="text-[11px] text-emerald-400 font-bold mt-1.5">Você economiza {formatBRL(pricing.save)}</div>
                      )}
                      <div className="flex-1" />
                      <button
                        onClick={() => nav(`/comprar-plano/${p.id}`)}
                        className="btn-gold w-full mt-4 py-2.5 rounded-md text-xs uppercase tracking-wider font-bold"
                        data-testid={`bh-buy-${p.id}`}
                      >Comprar</button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section className="py-20 bg-[#0D1530]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-3 text-center">FAQ</div>
          <h2 className="font-display text-2xl sm:text-3xl text-white text-center mb-10">Perguntas frequentes</h2>
          <div className="space-y-3" data-testid="bh-faq">
            {FAQS.map((f, i) => (
              <details key={i} className="card-marketing px-5 py-4 group">
                <summary className="cursor-pointer text-white font-semibold text-sm flex items-center justify-between list-none">
                  {f.q}
                  <CheckCircle2 size={15} className="text-[#D4AF37] shrink-0 ml-3 opacity-50 group-open:opacity-100" />
                </summary>
                <p className="text-sm text-white/60 leading-relaxed mt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CTA FINAL ═══ */}
      <section className="py-24 bg-[#0A1128] text-center border-t border-[#D4AF37]/20">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-display text-3xl sm:text-4xl text-white font-light leading-snug">
            Pare de pagar por um <span className="italic text-[#D4AF37]">escritório vazio.</span>
          </h2>
          <p className="text-white/60 mt-5 leading-relaxed">
            Tenha um escritório premium pagando apenas pelas horas que realmente utiliza.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10">
            <a href="#planos" className="btn-gold inline-flex items-center gap-2 px-8 py-4 rounded-md text-sm uppercase tracking-wider font-bold" data-testid="final-cta">
              Escolher meu Banco de Horas <ArrowRight size={16} />
            </a>
            <Link to="/contato" className="btn-ghost-gold inline-flex items-center gap-2 px-8 py-4 rounded-md text-sm uppercase tracking-wider font-bold">
              Falar com a equipe
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
